import { api } from '../api.js';
import { auth } from '../auth.js';
import { escapeHtml, showToast, formatDate } from '../utils.js';

/**
 * Family account management. Both guards v1 had are enforced server-side (you cannot
 * delete yourself, and you cannot remove the last admin); the buttons are disabled here
 * as well so the UI doesn't offer an action that will be refused.
 */

let users = [];

const app = () => document.getElementById('app');

export async function renderAdmin() {
    app().innerHTML = `
        <div class="container">
            <div class="section-header">
                <h1>Family accounts</h1>
                <button type="button" id="add-user-btn" class="btn btn-primary">
                    <i class="fa-solid fa-user-plus"></i> Add account
                </button>
            </div>
            <div id="admin-body"><p class="text-muted">Loading accounts...</p></div>
        </div>`;

    document.getElementById('add-user-btn').addEventListener('click', () => openUserModal(null));
    await loadUsers();
}

async function loadUsers() {
    const body = document.getElementById('admin-body');
    try {
        users = await api.listUsers();
    } catch (err) {
        body.innerHTML = `<div class="error-state"><p>${escapeHtml(err.message ?? 'Could not load accounts')}</p></div>`;
        return;
    }

    const me = auth.getUser();
    const adminCount = users.filter((u) => u.is_admin).length;

    body.innerHTML = `
        <div class="table-responsive">
            <table class="data-table">
                <thead>
                    <tr>
                        <th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Joined</th><th></th>
                    </tr>
                </thead>
                <tbody>
                    ${users.map((u) => rowHtml(u, me, adminCount)).join('')}
                </tbody>
            </table>
        </div>`;

    body.querySelectorAll('[data-action]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const id = Number(btn.closest('tr').dataset.id);
            const user = users.find((u) => u.id === id);
            if (!user) return;
            if (btn.dataset.action === 'edit') openUserModal(user);
            else if (btn.dataset.action === 'reset-mfa') resetMfa(user);
            else if (btn.dataset.action === 'delete') deleteUser(user);
        });
    });
}

function rowHtml(u, me, adminCount) {
    const isSelf = u.id === me?.id;
    // Mirrors the server-side guards so the UI never offers a refused action.
    const cannotDelete = isSelf || (u.is_admin && adminCount <= 1);
    const deleteTitle = isSelf
        ? 'You cannot delete your own account'
        : u.is_admin && adminCount <= 1
            ? 'This is the last admin account'
            : 'Delete account';

    return `
        <tr data-id="${u.id}">
            <td>${escapeHtml(u.full_name)}${isSelf ? ' <span class="text-muted">(you)</span>' : ''}</td>
            <td>${escapeHtml(u.email)}</td>
            <td><span class="badge ${u.is_admin ? 'badge-admin' : 'badge-user'}">${u.is_admin ? 'Admin' : u.account_type === 'limited' ? 'Limited' : 'Member'}</span></td>
            <td><span class="badge ${u.is_active ? 'badge-active' : 'badge-inactive'}">${u.is_active ? 'Active' : 'Disabled'}</span></td>
            <td>${escapeHtml(formatDate(u.created_at))}</td>
            <td class="table-actions">
                <button type="button" class="btn btn-sm btn-secondary" data-action="edit" title="Edit">
                    <i class="fa-solid fa-pen"></i>
                </button>
                <button type="button" class="btn btn-sm ${u.totp_enabled ? 'btn-warning' : 'btn-secondary'}"
                        data-action="reset-mfa" ${u.totp_enabled ? '' : 'disabled'}
                        aria-label="${u.totp_enabled ? `Two-factor is on for ${escapeHtml(u.full_name)} — turn it off` : `Two-factor is off for ${escapeHtml(u.full_name)}`}"
                        title="${u.totp_enabled ? 'Two-factor is on — click to turn it off (for a lost authenticator)' : 'Two-factor is off'}">
                    <i class="fa-solid fa-mobile-screen"></i>
                </button>
                <button type="button" class="btn btn-sm btn-danger" data-action="delete"
                        title="${escapeHtml(deleteTitle)}" ${cannotDelete ? 'disabled' : ''}>
                    <i class="fa-solid fa-trash"></i>
                </button>
            </td>
        </tr>`;
}

function openUserModal(user) {
    const isEdit = !!user;

    const overlay = document.createElement('div');
    overlay.className = 'modal show';
    overlay.innerHTML = `
        <div class="modal-content">
            <div class="modal-header">
                <h2>${isEdit ? 'Edit account' : 'Add account'}</h2>
                <button type="button" class="modal-close" aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">
                <form id="user-form" novalidate>
                    <div class="form-group">
                        <label for="u-name">Name</label>
                        <input type="text" id="u-name" value="${escapeHtml(user?.full_name ?? '')}" required>
                    </div>
                    <div class="form-group">
                        <label for="u-email">Email</label>
                        <input type="email" id="u-email" value="${escapeHtml(user?.email ?? '')}" required>
                        ${isEdit ? '' : '<p class="form-hint">They\'ll be emailed an invite with a temporary password.</p>'}
                    </div>
                    <div class="form-group">
                        <label for="u-type">Account type</label>
                        <select id="u-type" class="form-select">
                            <option value="family">Family member</option>
                            <option value="limited" ${user?.account_type === 'limited' ? 'selected' : ''}>Limited</option>
                        </select>
                        <p class="form-hint">Limited: no sharing either way, no co-admins, and nobody else's events unless they scan the QR code. For accounts outside the family, such as an app store reviewer.</p>
                    </div>
                    <div class="form-group setting-row">
                        <label><input type="checkbox" id="u-admin" ${user?.is_admin ? 'checked' : ''}> Administrator</label>
                        <p class="form-hint hidden" id="u-admin-hint">
                            A limited account cannot be an administrator: an admin can see every
                            account's email address, which is exactly what limited withholds.
                        </p>
                    </div>
                    ${isEdit ? `
                    <div class="form-group setting-row">
                        <label><input type="checkbox" id="u-active" ${user.is_active ? 'checked' : ''}> Account active</label>
                    </div>` : ''}
                    <div class="modal-actions">
                        <button type="submit" class="btn btn-primary">${isEdit ? 'Save changes' : 'Create account'}</button>
                    </div>
                </form>
            </div>
        </div>`;

    document.body.appendChild(overlay);
    document.body.classList.add('modal-open');

    const close = () => {
        overlay.remove();
        document.body.classList.remove('modal-open');
    };
    overlay.querySelector('.modal-close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    // Limited and administrator are mutually exclusive -- the API refuses the combination, so the
    // form must not offer it. Limited wins while it is selected; ticking Administrator puts the
    // type back to family rather than silently disagreeing with the tick.
    const typeSelect = overlay.querySelector('#u-type');
    const adminBox = overlay.querySelector('#u-admin');
    const adminHint = overlay.querySelector('#u-admin-hint');

    const syncAccountType = () => {
        const limited = typeSelect.value === 'limited';
        if (limited) adminBox.checked = false;
        adminBox.disabled = limited;
        adminHint.classList.toggle('hidden', !limited);
    };

    typeSelect.addEventListener('change', syncAccountType);
    adminBox.addEventListener('change', () => {
        if (adminBox.checked) typeSelect.value = 'family';
    });
    syncAccountType();

    overlay.querySelector('#user-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = e.target.querySelector('button[type=submit]');
        btn.disabled = true;

        const payload = {
            fullName: overlay.querySelector('#u-name').value.trim(),
            email: overlay.querySelector('#u-email').value.trim(),
            isAdmin: overlay.querySelector('#u-admin').checked,
            accountType: overlay.querySelector('#u-type').value,
        };
        if (isEdit) payload.isActive = overlay.querySelector('#u-active').checked;

        try {
            if (isEdit) await api.updateUser(user.id, payload);
            else await api.createUser(payload);
            close();
            showToast(isEdit ? 'Account updated' : 'Account created — invite sent', 'success');
            await loadUsers();
        } catch (err) {
            showToast(err.message ?? 'Could not save that account', 'error');
            btn.disabled = false;
        }
    });
}

async function resetMfa(user) {
    const isSelf = user.id === auth.getUser()?.id;
    const warning = isSelf
        ? 'Turn off two-factor on your OWN account? You will sign in with just a password until you set it up again.'
        : `Turn off two-factor for ${user.full_name}? They'll sign in with just a password until they set it up again, `
          + 'and will need to scan a new QR code — their old authenticator entry stops working.';
    if (!confirm(warning)) return;

    try {
        await api.disableUserMfa(user.id);
        showToast(`Two-factor turned off for ${user.full_name}`, 'success');
        await loadUsers();
    } catch (err) {
        showToast(err.message ?? 'Could not turn off two-factor', 'error');
    }
}

async function deleteUser(user) {
    if (!confirm(`Delete ${user.full_name}'s account? Their photos will be removed too. This cannot be undone.`)) return;
    try {
        await api.deleteUser(user.id);
        showToast('Account deleted', 'success');
        await loadUsers();
    } catch (err) {
        showToast(err.message ?? 'Could not delete that account', 'error');
    }
}
